import { IFluidCodeDetails } from '@fluidframework/core-interfaces';
import { CodeDetailsResolver, StreamCodeDetailsResolver } from './CreateHelpers/CodeDetailsResolvers';
import { fetchFromCDN } from './CreateHelpers/ContainerManifests';
import { ContainerManifest, ContainerManifestEntry } from '@fluidx/office-fluid-types';
import { isLocale, resolveLocale, safeLocalStorage } from '@fluidx/utilities';

/**
 * Top level API for retrieving specific container details created by hosts for a specific scenario
 */
export function getOneNoteMeetingsCodeDetails(): IFluidCodeDetails {
  return {
    package: {
      name: '@ms/onenote-meetings',
      version: '0.0.1',
      fluid: {
        browser: {
          umd: {
            files: [],
            library: ''
          }
        }
      }
    },
    config: {
      blueprintId: 'oneNoteMeeting'
    }
  };
}

/**
 * API for getting the Ch5 container code details
 */
export function getCh5CodeDetails(): IFluidCodeDetails {
  return {
    package: {
      name: '@fluidx/ch5-container',
      version: '0.0.1',
      fluid: {
        browser: {
          umd: {
            files: [],
            library: ''
          }
        }
      }
    }
  };
}

/**
 * API for getting the Catalog container code details
 */
export function getCatalogCodeDetails(): IFluidCodeDetails {
  return {
    package: {
      name: '@fluidx/catalog-container',
      version: '0.0.1',
      fluid: {
        browser: {
          umd: {
            files: [],
            library: ''
          }
        }
      }
    }
  };
}

/**
 * Allows external partners to use this codeloader to load specified container JS code
 * */
export function getFluidCodeDetailsOverride(): string | undefined {
  return safeLocalStorage()?.FluidCodeDetailsOverride;
}

/**
 * This method is used only to construct initial code details from half passed configurations.
 */
export function getOfficeCodeDetails(config: any = {}): IFluidCodeDetails {
  return {
    package: {
      name: '@ms/office-fluid-container',
      version: '0.0.1',
      fluid: {
        browser: {
          umd: {
            files: ['https://officefluiddev.blob.core.windows.net/staticcontent/versionless/container/codeLoader.js'],
            library: 'codeLoader'
          }
        }
      }
    },
    config
  };
}

// Resolvers that return IFluidCodeDetails that the BohemiaCodeLoader knows how to load
const supportedResolvers: CodeDetailsResolver[] = [StreamCodeDetailsResolver];

/**
 * Helper method to run url parsing logic on a URL and return container creation information
 */
export async function tryResolveContainerInfo(url: URL): Promise<IFluidCodeDetails | undefined> {
  // The for loop may not run in a particular order for async calls, so no guarenteed ordering. URLs should not be resolved by multiple resolvers.
  let resolved: IFluidCodeDetails | undefined;
  for (const resolver of supportedResolvers) {
    try {
      resolved = await resolver.resolve(url);
    } catch {
      resolved = undefined;
    }

    if (resolved !== undefined) {
      return resolved;
    }
  }

  return undefined;
}

const manifestLocaleIdentifier = '{locale}';
const manifestBaseUrl = 'https://cdn.fluidpreview.office.net/fluid/prod/container/versionless';

const supportedContainerManifestUrls: { genericUrl: string; localizableUrl?: string }[] = [
  {
    genericUrl: `${manifestBaseUrl}/officeContainerManifest.json`,
    localizableUrl: `${manifestBaseUrl}/officeContainerManifest.${manifestLocaleIdentifier}.json`
  }
];

/**
 * A method that returns all entry information from component manifests
 */
export async function getContainerManifestEntries(locale?: string): Promise<ContainerManifestEntry[]> {
  const resolvedLocale = isLocale(locale) ? resolveLocale(locale) : undefined;

  const manifests = await Promise.all(
    supportedContainerManifestUrls.map(async (manifestUrls) => {
      // First, we try to resolve the localized version if we have a localized url available and we were given a locale.
      if (resolvedLocale !== undefined && manifestUrls.localizableUrl !== undefined) {
        const resolvedLocalizedManifest = await fetchFromCDN(
          manifestUrls.localizableUrl.replace(manifestLocaleIdentifier, resolvedLocale)
        );

        // We only return if we are able to resolve something, otherwise we try to resolve the generic url instead.
        if (resolvedLocalizedManifest !== undefined) {
          return resolvedLocalizedManifest;
        }
      }

      return fetchFromCDN(manifestUrls.genericUrl);
    })
  );

  // There should be a smarter way of doing a map but removing undefined values... shouldn't there?
  const realManifests = manifests.filter((manifest) => manifest !== undefined) as ContainerManifest[];

  let entries: ContainerManifestEntry[] = [];
  realManifests.forEach((containerManifest) =>
    containerManifest.configurations.forEach((configuration) =>
      entries.push({
        codeDetails: { package: containerManifest.fluidCodeDetailsPackage, config: configuration.config },
        queryableData: configuration.queryableData,
        heroConfiguration: configuration.heroConfiguration
      })
    )
  );

  return entries;
}
